import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Iconify from "src/components/iconify";
import { useGetAccountDetailsQuery } from "src/services/account/account.service";
import { closeAccountViewer } from "src/store/slices/app/appSlice";
import { useAppDispatch } from "src/store/store";

type Props = {
    accountId: string;
    open: boolean;
};

const AccountInfoDialog = ({ open, accountId }: Props) => {

    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const { currentData: accountDetails, isLoading, isError } = useGetAccountDetailsQuery(accountId, {
        skip: !open || !accountId,
    });

    const handleClose = () => {
        dispatch(closeAccountViewer());
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            scroll="paper"
            aria-labelledby="account-viewer-title"
            aria-describedby="account-viewer-description"
        >
            <DialogContent sx={{ p: 0, position: 'relative' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1
                    }}
                >
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        sx={{ ml: 1 }}
                    >
                        <Iconify icon="mingcute:close-line" />
                    </IconButton>
                </Box>

                {isLoading && (
                    <Stack spacing={3} alignItems="center" py={4}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="text.secondary">
                            {t('account.viewer.loading')}
                        </Typography>
                    </Stack>
                )}

                {isError && (
                    <Stack spacing={3} alignItems="center" py={4}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center'
                            }}
                        >
                            <Iconify
                                icon="mingcute:warning-fill"
                                sx={{
                                    width: 48,
                                    height: 48,
                                    color: 'error.main',
                                    mb: 2
                                }}
                            />
                            <Typography variant="h6" color="error" gutterBottom>
                                {t('account.viewer.error')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {t('common.error')}
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={() => window.location.reload()}
                                startIcon={<Iconify icon="mingcute:refresh-2-line" />}
                            >
                                {t('account.viewer.retry')}
                            </Button>
                        </Box>
                    </Stack>
                )}

                {!isLoading && !isError && accountDetails && (
                    <Stack spacing={3}>
                        <Card variant="outlined">
                            <CardContent>
                                <Stack spacing={3}>
                                    {/* Profile Image and Basic Info */}
                                    <Stack direction="column" spacing={3} alignItems="center" justifyContent="center">
                                        <Avatar
                                            alt={accountDetails ? accountDetails.first_name : undefined}
                                            src={accountDetails ? accountDetails?.profile_image?.public_path : undefined}
                                            sx={{
                                                width: 80,
                                                height: 80,
                                                bgcolor: 'primary.main',
                                                fontSize: '2rem'
                                            }}
                                        >
                                            {accountDetails && !accountDetails.profile_image?.public_path
                                                ? `${accountDetails.first_name?.charAt(0)}${accountDetails.last_name?.charAt(0)}`
                                                : undefined}
                                        </Avatar>

                                        <Stack flex={1} alignItems="center" spacing={1}>
                                            <Typography variant="h6" component="h2">
                                                {accountDetails.first_name && accountDetails.last_name
                                                    ? `${accountDetails.first_name} ${accountDetails.last_name}`
                                                    : t('account.viewer.placeholders.no_name')
                                                }
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {accountDetails.email || t('account.viewer.placeholders.no_email')}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    )

}

export default AccountInfoDialog;